/**
 * ClickReel SDK - Video Utility Functions
 */

import type { VideoConfig } from './types';

// =============================================================================
// Sprint 001 (2026-04-29): Thumbnail fallback chain
// =============================================================================

/**
 * Identifier used in fallback chains to indicate "no URL — render the branded
 * fallback gradient + platform icon for this platform". Always the LAST entry
 * in any chain returned by `getThumbnailFallbackChain`.
 *
 * Format: `gradient:<videoType>` where videoType is youtube|instagram|tiktok|
 * html5|cloudflare. Consumers should call `parseGradientMarker()` to extract.
 */
export const GRADIENT_PREFIX = 'gradient:';

export type ThumbnailPlatform =
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'html5'
  | 'cloudflare';

/**
 * Returns true if the given chain entry is a branded-gradient sentinel rather
 * than an actual image URL.
 */
export function isGradientMarker(entry: string): boolean {
  return typeof entry === 'string' && entry.startsWith(GRADIENT_PREFIX);
}

/**
 * Extract the platform from a `gradient:<platform>` sentinel. Returns
 * 'html5' for unknown markers (safe default — generic dark gradient + play icon).
 */
export function parseGradientMarker(entry: string): ThumbnailPlatform {
  if (!isGradientMarker(entry)) return 'html5';
  const tail = entry.slice(GRADIENT_PREFIX.length).toLowerCase();
  if (
    tail === 'youtube' ||
    tail === 'instagram' ||
    tail === 'tiktok' ||
    tail === 'html5' ||
    tail === 'cloudflare'
  ) {
    return tail;
  }
  return 'html5';
}

/**
 * Infer the platform for a video config when `videoType` isn't explicitly set.
 * Used to pick the right branded gradient/icon.
 */
function inferPlatform(video: Partial<VideoConfig>): ThumbnailPlatform {
  if (video.videoType) return video.videoType;
  if (video.youtubeId) return 'youtube';
  if (video.cloudflareStreamUid) return 'cloudflare';
  if (video.tiktokId) return 'tiktok';
  if (video.instagramPostUrl || video.instagramVideoUrl) return 'instagram';
  return 'html5';
}

/**
 * Sprint 009 (2026-04-29): Optional configuration to prepend a server-side
 * thumbnail-proxy URL as the FIRST entry in the fallback chain. When the
 * proxy is up, visitors get cached image bytes served from `apiUrl` —
 * insulating them from third-party CDN failures and TikTok signed-URL
 * expiry. When the proxy is down or 404s, the chain falls through to the
 * existing direct-CDN tiers exactly as before, so this is strictly additive.
 */
export interface ThumbnailFallbackChainOptions {
  proxy?: {
    enabled: boolean;
    /** Base URL of the ingest API, e.g. `https://api.clickreel.io`. */
    apiUrl: string;
  };
}

/**
 * Internal: derive the {platform, id} pair the proxy expects, given a video
 * config. Returns null when no usable identifier is available — for those
 * videos the proxy can't help, so we skip the prepend and rely on the
 * existing chain.
 */
function getProxyIdentity(
  video: Partial<VideoConfig>,
  platform: ThumbnailPlatform,
): { platform: ResolverPlatform; id: string } | null {
  if (platform === 'youtube' && video.youtubeId) {
    return { platform: 'youtube', id: video.youtubeId };
  }
  if (platform === 'tiktok' && video.tiktokId) {
    return { platform: 'tiktok', id: video.tiktokId };
  }
  if (platform === 'cloudflare' && video.cloudflareStreamUid) {
    return { platform: 'cloudflare', id: video.cloudflareStreamUid };
  }
  // Instagram requires a post slug; we don't currently store one separately
  // from the URL, so skip the proxy for Instagram in this release. The chain
  // still falls back to thumbnailUrl + branded gradient as before.
  return null;
}

/** Strict subset matching the platform values accepted by the proxy endpoint. */
type ResolverPlatform = 'youtube' | 'tiktok' | 'instagram' | 'cloudflare';

/**
 * Compose the proxy URL: `{apiUrl}/v1/thumbnails/{platform}/{id}`.
 * Strips a trailing slash from apiUrl so we don't emit `//v1/...`.
 */
function buildProxyUrl(apiUrl: string, platform: ResolverPlatform, id: string): string {
  const base = apiUrl.replace(/\/+$/, '');
  return `${base}/v1/thumbnails/${platform}/${encodeURIComponent(id)}`;
}

/**
 * Build the ordered list of thumbnail URLs the SDK should attempt to render
 * before falling through to a branded gradient. Always returns at least one
 * entry — the trailing `gradient:<platform>` sentinel — so the bubble will
 * never be left with an empty image container.
 *
 * Behaviour:
 * - When `options.proxy.enabled` is true AND a `{platform, id}` pair can be
 *   derived (Sprint 009), prepends a `/v1/thumbnails/{platform}/{id}` URL as
 *   tier-1. Direct CDN URLs remain tier-2 fallbacks so a proxy outage
 *   degrades cleanly to the prior Sprint 001 behaviour.
 * - If the server provided `video.thumbnailFallbacks`, that array is preferred
 *   verbatim. A trailing `gradient:` marker is appended if none is present so
 *   the chain always terminates in a visual fallback. (Server-emitted chains
 *   take precedence over the per-video proxy prepend.)
 * - YouTube videos cascade through the four CDN tiers
 *   (maxres → sd → hq → mq) before falling to the gradient.
 * - Cloudflare videos use the synchronous thumbnail URL synthesised from the
 *   stream UID.
 * - Other platforms use `video.thumbnailUrl` if present.
 *
 * @example
 *   getThumbnailFallbackChain({ id: 'v1', url: '...', orientation: 'portrait', youtubeId: 'abc123' })
 *     // → ['https://img.youtube.com/vi/abc123/maxresdefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/sddefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/hqdefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/mqdefault.jpg',
 *     //    'gradient:youtube']
 *
 * @example with proxy enabled
 *   getThumbnailFallbackChain(
 *     { id: 'v1', url: '...', orientation: 'portrait', youtubeId: 'abc123' },
 *     { proxy: { enabled: true, apiUrl: 'https://api.clickreel.io' } },
 *   )
 *     // → ['https://api.clickreel.io/v1/thumbnails/youtube/abc123',
 *     //    'https://img.youtube.com/vi/abc123/maxresdefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/sddefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/hqdefault.jpg',
 *     //    'https://img.youtube.com/vi/abc123/mqdefault.jpg',
 *     //    'gradient:youtube']
 */
export function getThumbnailFallbackChain(
  video: Partial<VideoConfig> | null | undefined,
  options?: ThumbnailFallbackChainOptions,
): string[] {
  const v = video || {};
  const platform = inferPlatform(v);

  // Sprint 009: optional proxy prepend. We compute it once here and splice
  // it onto the front of whichever chain we end up returning. We do NOT
  // prepend ahead of a server-emitted thumbnailFallbacks override, because
  // that field is the explicit "trust the server's chain verbatim" signal.
  const proxyEnabled = options?.proxy?.enabled === true && typeof options.proxy.apiUrl === 'string' && options.proxy.apiUrl.length > 0;
  let proxyUrl: string | null = null;
  if (proxyEnabled) {
    const identity = getProxyIdentity(v, platform);
    if (identity) {
      proxyUrl = buildProxyUrl(options!.proxy!.apiUrl, identity.platform, identity.id);
    }
  }

  // Server-provided override: trust it, but guarantee a terminal gradient.
  if (Array.isArray(v.thumbnailFallbacks) && v.thumbnailFallbacks.length > 0) {
    const chain = v.thumbnailFallbacks.filter((s) => typeof s === 'string' && s.length > 0);
    if (chain.length === 0) return [`${GRADIENT_PREFIX}${platform}`];
    const last = chain[chain.length - 1];
    if (!isGradientMarker(last)) {
      chain.push(`${GRADIENT_PREFIX}${platform}`);
    }
    return chain;
  }

  const chain: string[] = [];
  if (proxyUrl) chain.push(proxyUrl);

  // YouTube cascade: maxres is highest quality but absent for many videos
  // (YouTube returns a 120x90 placeholder image for missing tiers, but newer
  // accounts get a true 404). The cascade walks down to the always-present
  // mqdefault before giving up.
  if (platform === 'youtube' && v.youtubeId) {
    const id = v.youtubeId;
    chain.push(`https://img.youtube.com/vi/${id}/maxresdefault.jpg`);
    chain.push(`https://img.youtube.com/vi/${id}/sddefault.jpg`);
    chain.push(`https://img.youtube.com/vi/${id}/hqdefault.jpg`);
    chain.push(`https://img.youtube.com/vi/${id}/mqdefault.jpg`);
    chain.push(`${GRADIENT_PREFIX}youtube`);
    return chain;
  }

  // Cloudflare: thumbnailUrl is synchronously computable from the stream UID
  // even if the row was created before the webhook fired. Server-side init now
  // emits this same URL into thumbnailUrl, so we just use whatever is present.
  if (platform === 'cloudflare') {
    if (v.thumbnailUrl && !isGradientMarker(v.thumbnailUrl)) chain.push(v.thumbnailUrl);
    chain.push(`${GRADIENT_PREFIX}cloudflare`);
    return chain;
  }

  // Generic path (TikTok, Instagram, HTML5, plus any defaults):
  // honour an explicit URL when present, otherwise go straight to gradient.
  if (v.thumbnailUrl && !isGradientMarker(v.thumbnailUrl)) {
    chain.push(v.thumbnailUrl);
  }
  chain.push(`${GRADIENT_PREFIX}${platform}`);
  return chain;
}

// =============================================================================
// H10 (2026-04-17): Safe CSS url() helper
// =============================================================================

/**
 * Sanitize a URL for safe interpolation into a CSS `url('...')` value.
 *
 * Background: thumbnail URLs from the ingest API are interpolated into
 * `style.cssText` as `background-image: url('${thumbnailUrl}')`. A malicious
 * or corrupted URL containing an unescaped `'` or `)` could break out of the
 * CSS context, enabling CSS-based data exfiltration via additional `url()`
 * fetches or style overrides.
 *
 * This helper returns:
 *  - The original URL if it is an absolute `http://` / `https://` URL, or a
 *    `data:image/*` URI, with no CSS-breaking characters.
 *  - An empty string otherwise (callers should treat empty as "no thumbnail").
 */
export function safeCssUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '';
  // Reject any char that could escape the url('...') context in style.cssText.
  if (/['"()\r\n\t\\\0]/.test(url)) return '';
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return url;
    if (parsed.protocol === 'data:' && /^data:image\//i.test(url)) return url;
    return '';
  } catch {
    return '';
  }
}

// =============================================================================
// Shared isMobile detection with cached result
// =============================================================================

let _isMobileCached: boolean | null = null;

function _computeIsMobile(): boolean {
  return window.innerWidth < 768;
}

// Sprint 005 (2026-04-29): named handler so it can be unbound by
// `teardownVideoUtils()` when the SDK is destroyed. Previously the listener
// was an anonymous arrow function with no removable reference, leaking on
// every SDK destroy/re-init cycle in SPA hosts.
function _invalidateIsMobileCache(): void {
  _isMobileCached = null;
}

// Invalidate cache on resize/orientation change
if (typeof window !== 'undefined') {
  window.addEventListener('resize', _invalidateIsMobileCache, { passive: true });
}

/**
 * Cached isMobile check. Result is computed once and invalidated on resize.
 */
export function isMobile(): boolean {
  if (_isMobileCached === null) {
    _isMobileCached = _computeIsMobile();
  }
  return _isMobileCached;
}

/**
 * Sprint 005 (2026-04-29): tear down the module-level resize listener and
 * reset the cached isMobile result. Call from `ClickReelSDK.destroy()` so
 * SPA hosts that destroy/re-init the SDK don't accumulate listeners.
 *
 * The listener is re-attached automatically on next module load if the
 * SDK is fully reloaded; for in-memory destroy/re-init within a single
 * page lifecycle, the listener stays detached after teardown — callers
 * who need fresh mobile detection should re-init the SDK.
 */
export function teardownVideoUtils(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', _invalidateIsMobileCache);
  }
  _isMobileCached = null;
}

// =============================================================================
// Shared YouTube IFrame API loader (Promise-based singleton)
// =============================================================================

let _ytApiPromise: Promise<void> | null = null;

/**
 * Load the YouTube IFrame API script. Returns a Promise that resolves when
 * window.YT.Player is available. Only loads the script once; subsequent
 * calls return the same Promise.
 */
export function loadYouTubeApi(): Promise<void> {
  if (_ytApiPromise) return _ytApiPromise;

  _ytApiPromise = new Promise<void>((resolve) => {
    // Already loaded
    if ((window as any).YT && (window as any).YT.Player) {
      resolve();
      return;
    }

    // Set up callback
    const existingCallback = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      if (existingCallback) existingCallback();
      resolve();
    };

    // Only add the script tag if not already present
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return _ytApiPromise;
}

/**
 * Extract the shortcode from an Instagram URL (reel or post)
 */
export function extractInstagramShortcode(url: string): string | null {
  if (!url) return null;
  const match = url.match(/instagram\.com\/(?:[^/]+\/)?(?:reels?|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
}

/**
 * Get the direct Instagram embed URL for a post/reel
 */
export function getInstagramEmbedUrl(postUrl: string): string | null {
  const shortcode = extractInstagramShortcode(postUrl);
  if (!shortcode) return null;
  if (/\/reels?\//.test(postUrl)) {
    return `https://www.instagram.com/reel/${shortcode}/embed/?captioned=false`;
  }
  return `https://www.instagram.com/p/${shortcode}/embed/?captioned=false`;
}

/**
 * Returns true if the URL is a TikTok short URL (e.g., tiktok.com/t/... or vm.tiktok.com/...)
 */
export function isTikTokShortUrl(url: string): boolean {
  return /tiktok\.com\/t\//i.test(url) || /vm\.tiktok\.com/i.test(url);
}

/**
 * Returns true if the given string is a numeric TikTok video ID (not a short URL shortcode)
 */
export function isTikTokNumericId(id: string): boolean {
  return /^\d+$/.test(id);
}

/**
 * Detect connection quality using Network Information API
 */
export function getConnectionQuality(): 'fast' | 'medium' | 'slow' | 'unknown' {
  const conn =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;
  if (!conn) return 'unknown';
  if (conn.saveData) return 'slow';
  const effectiveType = conn.effectiveType;
  if (effectiveType === '4g') return 'fast';
  if (effectiveType === '3g') return 'medium';
  if (effectiveType === '2g' || effectiveType === 'slow-2g') return 'slow';
  return 'unknown';
}

/**
 * SVG icon for muted state (speaker with slash)
 */
export function getMuteOverlayIcon(): string {
  return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
    <line x1="23" y1="9" x2="17" y2="15"></line>
    <line x1="17" y1="9" x2="23" y2="15"></line>
  </svg>`;
}

/**
 * Create a tap-to-unmute overlay element
 */
export function createUnmuteOverlay(
  onUnmute: () => void,
  container: HTMLElement,
): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'clickreel-unmute-overlay';
  overlay.innerHTML = `
    <div class="clickreel-unmute-pill">
      ${getMuteOverlayIcon()}
      <span>Tap to unmute</span>
    </div>
  `;
  overlay.addEventListener('click', (e) => {
    e.stopPropagation();
    onUnmute();
    overlay.classList.add('clickreel-unmute-hiding');
    setTimeout(() => overlay.remove(), 300);
  });
  container.appendChild(overlay);
  return overlay;
}

/**
 * Create a buffering spinner overlay
 */
export function createBufferingOverlay(container: HTMLElement): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'clickreel-buffering-overlay';
  overlay.innerHTML = `<div class="clickreel-buffering-spinner"></div>`;
  container.appendChild(overlay);
  return overlay;
}

/**
 * Create a "Tap to load" placeholder for slow connections
 */
export function createTapToLoadPlaceholder(
  onTap: () => void,
  thumbnailUrl?: string,
): HTMLElement {
  const placeholder = document.createElement('div');
  placeholder.className = 'clickreel-tap-to-load';
  if (thumbnailUrl) {
    placeholder.style.backgroundImage = `url('${thumbnailUrl}')`;
    placeholder.style.backgroundSize = 'cover';
    placeholder.style.backgroundPosition = 'center';
  }
  placeholder.innerHTML = `
    <div class="clickreel-tap-to-load-btn">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
        <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
      </svg>
      <span>Tap to load video</span>
    </div>
  `;
  placeholder.addEventListener('click', (e) => {
    e.stopPropagation();
    onTap();
    placeholder.remove();
  });
  return placeholder;
}

/**
 * Inject shared utility CSS styles into shadow root (idempotent)
 */
export function injectUtilityStyles(shadow: ShadowRoot): void {
  if (shadow.querySelector('style[data-utils]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-utils', 'true');
  style.textContent = `
    /* Tap-to-unmute overlay */
    .clickreel-unmute-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 25;
      pointer-events: auto;
      cursor: pointer;
      animation: clickreel-unmute-fade-in 0.3s ease;
    }
    .clickreel-unmute-overlay.clickreel-unmute-hiding {
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    .clickreel-unmute-pill {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: rgba(0,0,0,0.7);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 24px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      animation: clickreel-unmute-pulse 2s ease-in-out infinite;
      position: absolute;
      bottom: 33%;
    }
    @keyframes clickreel-unmute-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    @keyframes clickreel-unmute-fade-in {
      from { opacity: 0; } to { opacity: 1; }
    }

    /* Buffering spinner */
    .clickreel-buffering-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 12;
      pointer-events: none;
      background: rgba(0,0,0,0.3);
    }
    .clickreel-buffering-overlay.hidden { display: none; }
    .clickreel-buffering-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255,255,255,0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: clickreel-buf-spin 0.8s linear infinite;
    }
    @keyframes clickreel-buf-spin {
      to { transform: rotate(360deg); }
    }

    /* Tap to load placeholder */
    .clickreel-tap-to-load {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 12;
      background: #111;
      cursor: pointer;
    }
    .clickreel-tap-to-load-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      opacity: 0.9;
    }

    /* Story progress bar */
    .clickreel-story-bar {
      position: absolute;
      top: 8px;
      left: 12px;
      right: 12px;
      display: flex;
      gap: 3px;
      z-index: 20;
    }
    .clickreel-story-segment {
      flex: 1;
      height: 3px;
      border-radius: 2px;
      background: rgba(255,255,255,0.4);
      overflow: hidden;
    }
    .clickreel-story-segment.completed { background: white; }
    .clickreel-story-segment.active { background: rgba(255,255,255,0.4); }
    .clickreel-story-fill {
      height: 100%;
      background: white;
      width: 0%;
      transition: width 0.3s linear;
    }
    .clickreel-story-counter {
      position: absolute;
      top: 18px;
      right: 56px;
      color: white;
      font-size: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: rgba(0,0,0,0.5);
      padding: 2px 8px;
      border-radius: 10px;
      z-index: 20;
    }
    .clickreel-story-nav {
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 18;
      cursor: pointer;
    }
    .clickreel-story-nav-left { left: 0; width: 30%; }
    .clickreel-story-nav-right { right: 0; width: 70%; }
    .clickreel-story-chevron {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      color: white;
      opacity: 0;
      transition: opacity 0.2s;
    }
    .clickreel-story-nav:hover .clickreel-story-chevron { opacity: 0.6; }
    .clickreel-story-nav-left .clickreel-story-chevron { left: 8px; }
    .clickreel-story-nav-right .clickreel-story-chevron { right: 8px; }

    /* Social proof badge */
    .clickreel-social-proof {
      position: absolute;
      z-index: 15;
      padding: 6px 12px;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 20px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
      opacity: 0;
      animation: clickreel-social-proof-in 0.4s ease 1.5s forwards;
    }
    .clickreel-social-proof.top-left { top: 12px; left: 12px; }
    .clickreel-social-proof.top-right { top: 12px; right: 56px; }
    .clickreel-social-proof.bottom-left { bottom: 12px; left: 12px; }
    @keyframes clickreel-social-proof-in {
      from { opacity: 0; transform: scale(0.9); }
      to { opacity: 1; transform: scale(1); }
    }

    /* Product card slide-up */
    .clickreel-product-card {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10;
      transform: translateY(100%);
      transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: auto;
    }
    .clickreel-product-card.visible {
      transform: translateY(0);
    }
    .clickreel-product-card-inner {
      background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 70%, transparent 100%);
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .clickreel-product-card-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .clickreel-product-card-img {
      width: 60px;
      height: 60px;
      border-radius: 10px;
      object-fit: cover;
      flex-shrink: 0;
    }
    .clickreel-product-card-info {
      flex: 1;
      min-width: 0;
    }
    .clickreel-product-card-name {
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 15px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .clickreel-product-card-price {
      color: #4ade80;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 700;
    }
    .clickreel-product-card-badge {
      display: inline-block;
      padding: 2px 8px;
      background: rgba(251, 146, 60, 0.9);
      border-radius: 10px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
      font-weight: 600;
      margin-left: 8px;
    }
    .clickreel-product-card-cta {
      display: block;
      width: 100%;
      padding: 12px;
      border: none;
      border-radius: 10px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 15px;
      font-weight: 600;
      text-align: center;
      text-decoration: none;
      cursor: pointer;
      transition: filter 0.2s;
    }
    .clickreel-product-card-cta:hover { filter: brightness(1.1); }

    /* Equalizer bars for bubble */
    .clickreel-equalizer {
      position: absolute;
      bottom: 4px;
      right: 4px;
      display: flex;
      align-items: flex-end;
      gap: 2px;
      height: 14px;
      z-index: 3;
    }
    .clickreel-eq-bar {
      width: 3px;
      border-radius: 1px;
      animation: clickreel-eq 1s ease-in-out infinite;
    }
    .clickreel-eq-bar:nth-child(1) { animation-delay: 0s; }
    .clickreel-eq-bar:nth-child(2) { animation-delay: 0.2s; }
    .clickreel-eq-bar:nth-child(3) { animation-delay: 0.4s; }
    @keyframes clickreel-eq {
      0%, 100% { height: 4px; }
      50% { height: 12px; }
    }

    /* Exit pulse animation */
    @keyframes clickreel-exit-pulse {
      0% { transform: scale(1); }
      15% { transform: scale(1.15); }
      30% { transform: scale(1); }
      45% { transform: scale(1.15); }
      60% { transform: scale(1); }
      75% { transform: scale(1.15); }
      90%, 100% { transform: scale(1); }
    }
    .clickreel-exit-pulse {
      animation: clickreel-exit-pulse 1.8s ease;
    }

    /* Social proof badge on bubble */
    .clickreel-bubble-social-proof {
      position: absolute;
      bottom: -24px;
      left: 50%;
      transform: translateX(-50%) scale(0);
      white-space: nowrap;
      padding: 3px 8px;
      background: rgba(0,0,0,0.75);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 10px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11px;
      z-index: 3;
      animation: clickreel-social-proof-in 0.3s ease 1s forwards;
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Variant picker */
    .clickreel-variant-picker {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      padding: 4px 0;
      align-items: center;
    }
    .clickreel-variant-swatch {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.3);
      cursor: pointer;
      transition: border-color 0.2s, transform 0.2s;
      flex-shrink: 0;
    }
    .clickreel-variant-swatch.selected {
      border-color: white;
      transform: scale(1.1);
    }
    .clickreel-variant-swatch.unavailable {
      opacity: 0.4;
      cursor: default;
    }
    .clickreel-variant-chip {
      padding: 4px 10px;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.3);
      background: transparent;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 32px;
      min-height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .clickreel-variant-chip.selected {
      background: rgba(255,255,255,0.2);
      border-color: white;
    }
    .clickreel-variant-chip.unavailable {
      opacity: 0.4;
      text-decoration: line-through;
      cursor: default;
    }
    .clickreel-variant-more {
      color: rgba(255,255,255,0.6);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11px;
      padding: 0 4px;
    }
  `;
  shadow.appendChild(style);
}
